import { FC, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  Typography,
  Avatar,
  IconButton,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { useMutation, useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import { eachDayOfInterval, format, startOfDay } from 'date-fns';
import api from '../../utils/api';
import { executeWrite, executeUpdate, OfflineWriteBlockedError } from '../../offline/executeWrite';
import { useIsOffline } from '../../offline';
import { useRBAC } from '../../auth/useRBAC';

interface Veterinarian {
  id: string;
  name: string;
  firstName?: string;
  lastName?: string;
  professionalTitle?: string;
}

interface TimeBlockData {
  id?: string;
  startTime: string;
  endTime: string;
  reason?: string;
  /** null = clinic-wide closure (ADR-0028). */
  vetId: string | null;
  vet?: Veterinarian | null;
  rangeGroupId?: string | null;
}

interface AddTimeBlockDialogProps {
  open: boolean;
  onClose: () => void;
  selectedDate: Date | null;
  vet: Veterinarian | null;
  editingBlock?: TimeBlockData | null;
}

/** Fan-out cap — a typo'd end date must not queue a thousand creates. */
const MAX_RANGE_DAYS = 60;

const AddTimeBlockDialog: FC<AddTimeBlockDialogProps> = ({
  open,
  onClose,
  selectedDate,
  vet,
  editingBlock = null,
}) => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isOffline = useIsOffline();
  const { claims } = useRBAC();

  const [date, setDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [endTime, setEndTime] = useState<Date | null>(null);
  const [reason, setReason] = useState('');
  const [clinicWide, setClinicWide] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Closures are practice-level: OWNER/ADMIN only (server-enforced too).
  const canCreateClosure = claims?.role === 'OWNER' || claims?.role === 'ADMIN';
  const editingClosure = !!editingBlock && editingBlock.vetId == null;

  useEffect(() => {
    if (open) {
      if (editingBlock) {
        const start = new Date(editingBlock.startTime);
        const end = new Date(editingBlock.endTime);
        setDate(start);
        setEndDate(start); // edits stay single-day
        setStartTime(start);
        setEndTime(end);
        setReason(editingBlock.reason || '');
        setClinicWide(editingBlock.vetId == null);
      } else {
        const defaultDate = selectedDate || new Date();
        setDate(defaultDate);
        setEndDate(defaultDate);
        const defaultStart = new Date(defaultDate);
        defaultStart.setHours(9, 0, 0, 0);
        const defaultEnd = new Date(defaultDate);
        defaultEnd.setHours(17, 0, 0, 0);
        setStartTime(defaultStart);
        setEndTime(defaultEnd);
        setReason('');
        // Opened with no vet (the Schedule's "Close the whole clinic"): a closure is the only block it can make.
        setClinicWide(!vet && canCreateClosure);
      }
      setError(null);
    }
  }, [open, selectedDate, editingBlock, vet, canCreateClosure]);

  const combined = (d: Date, time: Date): Date => {
    const result = new Date(d);
    result.setHours(time.getHours(), time.getMinutes(), 0, 0);
    return result;
  };

  // One mutation fans a date range out into per-day blocks: rendering,
  // conflict checks, and per-day deletes all keep working unchanged, and a
  // shared rangeGroupId ties the rows together for delete-all-days.
  // Vet blocks ride the offline op-queue; closures are direct POSTs —
  // an offline-queued closure could silently shut the clinic days later
  // (the sync push also rejects them server-side, ADR-0028).
  const createMutation = useMutation(
    async ({ days, rangeGroupId }: { days: Date[]; rangeGroupId?: string }) => {
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const failed: string[] = [];
      let firstFailureReason: string | null = null;
      for (const day of days) {
        const payload = {
          startTime: combined(day, startTime!).toISOString(),
          endTime: combined(day, endTime!).toISOString(),
          reason: reason || undefined,
          ...(clinicWide ? {} : { vetId: vet!.id }),
          ...(rangeGroupId ? { rangeGroupId } : {}),
          timeZone,
        };
        try {
          if (clinicWide) {
            await api.post('/time-blocks', payload);
          } else {
            await executeWrite({
              entityType: 'timeBlock',
              table: 'timeBlocks',
              payload,
              networkFn: () => api.post('/time-blocks', payload).then(r => r.data),
            });
          }
        } catch (e: any) {
          if (e instanceof OfflineWriteBlockedError) throw e;
          failed.push(format(day, 'MMM d'));
          firstFailureReason = firstFailureReason ?? e.response?.data?.message ?? null;
        }
      }
      return { total: days.length, failed, firstFailureReason };
    },
    {
      onSuccess: ({ total, failed, firstFailureReason }) => {
        queryClient.invalidateQueries(['time-blocks']);
        if (failed.length > 0) {
          setError(
            `Blocked ${total - failed.length} of ${total} day${total === 1 ? '' : 's'} — failed: ${failed.join(', ')}` +
              (firstFailureReason ? ` (${firstFailureReason})` : '') +
              '. Adjust the dates to retry ONLY the failed days — re-submitting the same range duplicates the days that succeeded.',
          );
          return; // keep the dialog open so the partial range is visible
        }
        enqueueSnackbar(
          clinicWide
            ? `Clinic closed for ${total} day${total === 1 ? '' : 's'}`
            : total === 1
              ? 'Time block created successfully'
              : `Blocked ${total} days`,
          { variant: 'success' },
        );
        handleClose();
      },
      onError: (err: any) => {
        if (err instanceof OfflineWriteBlockedError) {
          setError(err.message);
          return;
        }
        setError(err.response?.data?.message || 'Failed to create time block');
      },
    }
  );

  const updateMutation = useMutation(
    async ({ id, data }: { id: string; data: { startTime?: string; endTime?: string; reason?: string; timeZone: string } }) => {
      // Closure edits are online-only (sync push rejects them) — direct PUT.
      if (editingClosure) {
        return api.put(`/time-blocks/${id}`, data).then(r => r.data);
      }
      return executeUpdate({
        entityType: 'timeBlock',
        table: 'timeBlocks',
        entityId: id,
        payload: data,
        networkFn: () => api.put(`/time-blocks/${id}`, data).then(r => r.data),
      });
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['time-blocks']);
        enqueueSnackbar(editingClosure ? 'Clinic closure updated' : 'Time block updated successfully', { variant: 'success' });
        handleClose();
      },
      onError: (err: any) => {
        if (err instanceof OfflineWriteBlockedError) {
          setError(err.message);
          return;
        }
        setError(err.response?.data?.message || 'Failed to update time block');
      },
    }
  );

  const handleClose = () => {
    setDate(null);
    setEndDate(null);
    setStartTime(null);
    setEndTime(null);
    setReason('');
    setClinicWide(false);
    setError(null);
    onClose();
  };

  const handleSubmit = () => {
    if (!date || !startTime || !endTime) return;
    if (!editingBlock && !clinicWide && !vet) return;

    const start = combined(date, startTime);
    const end = combined(date, endTime);
    if (end <= start) {
      setError('End time must be after start time');
      return;
    }

    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    if (editingBlock?.id) {
      updateMutation.mutate({
        id: editingBlock.id,
        data: {
          startTime: start.toISOString(),
          endTime: end.toISOString(),
          reason: reason || undefined,
          timeZone,
        },
      });
      return;
    }

    const lastDay = startOfDay(endDate ?? date);
    const firstDay = startOfDay(date);
    if (lastDay < firstDay) {
      setError('End date must be on or after the start date');
      return;
    }
    const days = eachDayOfInterval({ start: firstDay, end: lastDay });
    if (days.length > MAX_RANGE_DAYS) {
      setError(`Ranges are capped at ${MAX_RANGE_DAYS} days`);
      return;
    }
    createMutation.mutate({
      days,
      rangeGroupId: days.length > 1 ? crypto.randomUUID() : undefined,
    });
  };

  const isSubmitting = createMutation.isLoading || updateMutation.isLoading;
  const closureOfflineBlocked = (clinicWide || editingClosure) && isOffline;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" component="span">
          {editingClosure ? 'Edit Clinic Closure' : editingBlock ? 'Edit Time Block' : 'Block Time'}
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          {vet && !clinicWide && !editingClosure && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Avatar sx={{ width: 36, height: 36, fontSize: '0.85rem' }}>
                {vet.name.split(' ').map(n => n[0]).join('')}
              </Avatar>
              <Box>
                <Typography variant="body2" fontWeight={600}>{vet.name}</Typography>
                {vet.professionalTitle && (
                  <Typography variant="caption" color="text.secondary">{vet.professionalTitle}</Typography>
                )}
              </Box>
            </Box>
          )}

          {canCreateClosure && !editingBlock && (
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={clinicWide}
                    onChange={(e) => setClinicWide(e.target.checked)}
                    disabled={isOffline}
                  />
                }
                label="Close the whole clinic (holiday / closure)"
              />
              {isOffline && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Clinic closures need a connection.
                </Typography>
              )}
            </Box>
          )}

          {(clinicWide || editingClosure) && (
            <Alert severity="warning">
              This blocks <strong>every provider</strong> for the selected time — no appointments
              (assigned or unassigned) can be booked while the clinic is closed.
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2 }}>
            <DatePicker
              label={editingBlock ? 'Date' : 'Start date'}
              value={date}
              onChange={(newValue) => setDate(newValue)}
              slotProps={{ textField: { fullWidth: true } }}
            />
            {!editingBlock && (
              <DatePicker
                label="End date"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                minDate={date ?? undefined}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    helperText: 'Same as start date for a single day',
                  },
                }}
              />
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <TimePicker
              label="Start Time"
              value={startTime}
              onChange={(newValue) => setStartTime(newValue)}
              slotProps={{ textField: { fullWidth: true } }}
            />
            <TimePicker
              label="End Time"
              value={endTime}
              onChange={(newValue) => setEndTime(newValue)}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Box>
          {!editingBlock && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
              Multi-day ranges block these hours on each day.
            </Typography>
          )}

          <TextField
            label="Reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            rows={3}
            fullWidth
            placeholder={clinicWide ? 'e.g., Labor Day, Staff retreat, Snow closure...' : 'e.g., Lunch break, Personal time, Conference...'}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            !date ||
            !startTime ||
            !endTime ||
            (!editingBlock && !clinicWide && !vet) ||
            closureOfflineBlocked ||
            isSubmitting
          }
          sx={{
            backgroundColor: '#d32f2f',
            '&:hover': { backgroundColor: '#b71c1c' },
          }}
        >
          {isSubmitting
            ? 'Saving...'
            : editingBlock
              ? editingClosure ? 'Update Closure' : 'Update Block'
              : clinicWide
                ? 'Close Clinic'
                : 'Block Time'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddTimeBlockDialog;
